/**
 * Where a forced sign-out lands: the login page, with the `reason` its notice
 * explains (see <ForceLogoutAlert>). Pure, so the routing rule is testable.
 */
import type { Audience } from "../types.js";

/** Refresh refusals that mean the company's workspace is closed, not the session. */
const WORKSPACE_CLOSED_CODES: ReadonlySet<string> = new Set([
  "TENANT_SUSPENDED",
  "SUBSCRIPTION_PURGED",
  "SUBSCRIPTION_NOT_FOUND",
]);

/**
 * @param errorCode the refusal's `errorCode`
 * @param aud the app's audience
 * @param redirectTo the in-app path to come back to (read only when it is used)
 * @returns the path relative to the app's base URL, e.g. `login?reason=session_revoked&redirect=%2Fx`
 */
export function forceLogoutPath(
  errorCode: string,
  aud: Audience,
  redirectTo: () => string,
): string {
  // The refresh answered 403: this account no longer holds an
  // admin-capable role (demoted mid-session). Signing in again here is
  // impossible, and the deep link went with the role, so no `redirect`; the
  // notice points at the employee app. Admin-only: other apps' login routes
  // don't know this reason.
  if (aud === "admin" && errorCode === "AUTH_ADMIN_AUDIENCE_NOT_PERMITTED") {
    return "login?reason=admin_access_revoked";
  }
  // The workspace is closed (HR suspended or its cancellation grace over, data
  // purged, or no HR subscription): signed out like any refusal, and the login
  // page says why instead of the generic "signed out for security". No deep
  // link: it can't open until the workspace is reactivated.
  if (WORKSPACE_CLOSED_CODES.has(errorCode)) return "login?reason=workspace_closed";
  return `login?reason=session_revoked&redirect=${encodeURIComponent(redirectTo())}`;
}
